import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { database, ref, get, remove } from '../firebase';
import { FiFilm, FiTrash2, FiClock } from 'react-icons/fi';

const History = () => {
  const [historyVideos, setHistoryVideos] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedVideos, setSelectedVideos] = useState([]);

  useEffect(() => {
    const fetchHistory = async () => {
      // Fetch logged-in user from localStorage
      const userData = JSON.parse(localStorage.getItem('currentUser'));
      if (!userData || !userData.mobile) {
        setError('No logged-in user found. Please sign in to view your watch history.');
        setLoading(false);
        return;
      }

      // Sanitize mobile number for Firebase node name
      const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');

      try {
        // Fetch history from Firebase Realtime Database
        const historyRef = ref(database, `videoplayer/history/${sanitizedMobile}`);
        const snapshot = await get(historyRef);
        if (snapshot.exists()) {
          const historyData = snapshot.val();
          // Convert object to array of videos with historyId
          const videoArray = Object.keys(historyData).map((historyId) => ({
            historyId,
            ...historyData[historyId],
          }));
          // Sort by timestamp (most recent first)
          videoArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
          setHistoryVideos(videoArray);
        } else {
          setHistoryVideos([]);
        }
      } catch (err) {
        console.error('Error fetching history:', err);
        setError('Failed to load watch history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  // Handle checkbox toggle for selecting videos
  const handleSelectVideo = (historyId) => {
    setSelectedVideos((prev) =>
      prev.includes(historyId)
        ? prev.filter((id) => id !== historyId)
        : [...prev, historyId]
    );
  };

  // Remove selected videos from history
  const handleRemoveSelected = async () => {
    if (selectedVideos.length === 0) {
      setError('No videos selected for removal.');
      return;
    }

    const userData = JSON.parse(localStorage.getItem('currentUser'));
    if (!userData || !userData.mobile) {
      setError('No logged-in user found.');
      return;
    }

    const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');

    try {
      for (const historyId of selectedVideos) {
        const videoRef = ref(database, `videoplayer/history/${sanitizedMobile}/${historyId}`);
        await remove(videoRef);
      }
      // Update local state to remove deleted videos
      setHistoryVideos((prev) => prev.filter((video) => !selectedVideos.includes(video.historyId)));
      setSelectedVideos([]);
      setError('');
    } catch (err) {
      console.error('Error removing selected videos:', err);
      setError('Failed to remove selected videos. Please try again.');
    }
  };

  // Remove all videos from history
  const handleClearAllHistory = async () => {
    const userData = JSON.parse(localStorage.getItem('currentUser'));
    if (!userData || !userData.mobile) {
      setError('No logged-in user found.');
      return;
    }

    const sanitizedMobile = userData.mobile.replace(/[^0-9]/g, '_');

    try {
      const historyRef = ref(database, `videoplayer/history/${sanitizedMobile}`);
      await remove(historyRef);
      setHistoryVideos([]);
      setSelectedVideos([]);
      setError('');
    } catch (err) {
      console.error('Error clearing history:', err);
      setError('Failed to clear watch history. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-gray-200 dark:from-gray-900 dark:to-gray-800">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-indigo-600 dark:border-indigo-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-2 sm:px-4 md:px-8 py-4 bg-gradient-to-b from-indigo-50 to-gray-200 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto w-full">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4 sm:gap-0">
          <h2 className="text-2xl xs:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white animate-fade-in text-center sm:text-left">
            Watch History
          </h2>
          {historyVideos.length > 0 && (
            <div className="flex flex-row space-x-2 xs:space-x-4 w-full xs:w-auto flex-nowrap">
              <button
                onClick={handleRemoveSelected}
                className="flex items-center justify-center px-4 xs:px-5 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all duration-300 disabled:bg-red-400 disabled:cursor-not-allowed transform hover:scale-105 shadow-md hover:shadow-lg text-sm xs:text-base"
                disabled={selectedVideos.length === 0}
              >
                <FiTrash2 className="mr-2" size={18} />
                Remove Selected
              </button>
              <button
                onClick={handleClearAllHistory}
                className="flex items-center justify-center px-4 xs:px-5 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg text-sm xs:text-base"
              >
                <FiTrash2 className="mr-2" size={18} />
                Clear All History
              </button>
            </div>
          )}
        </div>
        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300 p-3 xs:p-4 rounded-xl mb-4 xs:mb-6 text-xs xs:text-sm border border-red-200 dark:border-red-700/50 animate-fade-in text-center">
            {error}
          </div>
        )}
        {historyVideos.length === 0 && !error ? (
          <div className="flex flex-col items-center justify-center h-56 xs:h-64 bg-white dark:bg-gray-800 rounded-2xl shadow-md animate-slide-up px-2 xs:px-0">
            <FiFilm className="text-gray-400 dark:text-gray-500 mb-4" size={40} />
            <p className="text-gray-600 dark:text-gray-300 text-base xs:text-lg font-medium text-center">
              You haven't watched any videos yet.
            </p>
            <Link
              to="/home"
              className="mt-3 xs:mt-4 text-indigo-600 dark:text-indigo-400 font-medium hover:underline transition-colors duration-200 text-sm xs:text-base"
            >
              Explore Videos
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 xs:gap-6">
            {historyVideos.map((video) => (
              <div
                key={video.historyId}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 animate-slide-up flex flex-col"
              >
                <Link to={`/video/${video.videoId}`}
                  className="block w-full">
                  <img
                    src={video.thumbnailURL}
                    alt={video.title}
                    className="w-full h-40 xs:h-48 object-cover rounded-t-xl"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/320x180?text=Thumbnail+Not+Found';
                    }}
                  />
                </Link>
                <div className="p-3 xs:p-4 flex items-start">
                  <input
                    type="checkbox"
                    checked={selectedVideos.includes(video.historyId)}
                    onChange={() => handleSelectVideo(video.historyId)}
                    className="mt-1 mr-2 xs:mr-3 h-5 w-5 xs:h-5 xs:w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded transition-all duration-200"
                  />
                  <div className="flex-1 min-w-0">
                    <Link to={`/video/${video.videoId}`}
                      className="block">
                      <h3 className="text-sm xs:text-md font-semibold text-gray-900 dark:text-white line-clamp-2 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors duration-200">
                        {video.title}
                      </h3>
                      <p className="text-xs xs:text-sm text-gray-500 dark:text-gray-400 mt-1 xs:mt-2 flex items-center">
                        <FiClock className="mr-1" size={13} />
                        Watched: {video.timestamp ? new Date(video.timestamp).toLocaleDateString() : 'N/A'}
                      </p>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.4s ease-out forwards;
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default History;